import {AxiosRequestConfig, RawAxiosResponseHeaders} from 'axios';

export enum MflUserEntityErrorCode {
  invalid = 'invalid',
  sign_up_too_young = 'sign_up_too_young',
  sign_up_already_signed_up = 'sign_up_already_signed_up',
  sign_up_email_address_invalid = 'sign_up_email_address_invalid',
  must_be_present = 'must_be_present',
  password_too_weak = 'password_too_weak',
  sign_up_form_password_too_weak = 'sign_up_form_password_too_weak',
  billing_address_locality_unknown = 'billing_address_locality_unknown',
  birth_date_too_old = 'birth_date_too_old',
  birth_date_too_young = 'birth_date_too_young',
}

/* Example
  {
    "errors": [
      {
        "code": "invalid",
        "detail": "is invalid",
        "source": { "pointer": "/user_identity/phone_number" }
      }
    ]
  }
*/

type MflErrorSource = {
  pointer?: string;
};

export type MflError = {
  code?: MflUserEntityErrorCode;
  detail?: string;
  source?: MflErrorSource;
};

// TODO: unify with Mfl

/* eslint-disable @typescript-eslint/no-explicit-any */
export type User = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  dateOfBirth?: string | null; // YYYY-MM-DD format
  phoneNumber?: string | null;
  billingAddress?: BillingAddress | null;
  password?: string | null;
  data?: any;
  status?: number;
  statusText?: string;
  headers?: RawAxiosResponseHeaders;
  config?: AxiosRequestConfig<any>;
  request?: any;
  passwordValidationBypass?: any;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export type BillingAddress = {
  lines: string[];
  postal_code: string;
  locality: string;
  administrative_area: string;
  country_code?: string;
};

// Types duplicated in middleware/.../mfl.ts

// Shop

export type Shop = {
  email?: string;
  name: string;
  phone: string;
  store_number: string;
  website?: string | null;
};

export type TradingHours = {
  open: boolean;
  from: string; // "09:00:00"
  until: string; // "23:00:00"
};

// Address

export type Address = {
  administrative_area: string;
  country_code?: string;
  custom1?: string;
  custom2?: string;
  lines: string[];
  locality: string;
  postal_code: string;
  unit?: string;
};

// Competition

export type Competition = {
  name: string;
  description: string;
  eligible_description: string;
  entered_description: string;
  retail_entry_allowed: boolean;
  image_dimensions: {
    width: number;
    height: number;
  };
};

// Transfer tokens

export type TransferTokens = {
  token: string;
  parameter: string;
  domain: string;
};

// Other: HTTP request and responses

type PostShopCompetitionEntriesRequestOptions = 'terms_and_conditions';

export type PostShopCompetitionEntriesRequest = {
  options: [PostShopCompetitionEntriesRequestOptions];
  order_id: string;
};

type Capability = 'three_d_secure_tokenisation' | 'three_d_secure_transaction';
export type CheckoutOrderRequest = {
  order: {
    payment_instrument_id: string;
    browser_information?: BrowserInformation;
    till_3ds_success_url?: string;
    till_3ds_cancel_url?: string;
    till_3ds_error_url?: string;
    capabilities?: Capability[];
  };
};

export type BrowserInformation = {
  accept_header?: string;
  ip_address?: string | null;
  java_enabled?: boolean;
  language?: string;
  color_depth?: number;
  screen_height?: number;
  screen_width?: number;
  timezone?: number;
  user_agent?: string;
};

export enum PaymentServiceType {
  Till = 'till_payments',
  ZenGiftCards = 'zen_gift_cards',
}

export type PaymentService = {
  id: string;
  instruments: boolean;
  name: string;
  service_type: PaymentServiceType;
  test: boolean;
  type: string;
};

type BasePaymentInstrument = {
  type: string;
  id: string;
  test: boolean;
  service_type: PaymentServiceType;
  expired: boolean;
  service: {
    type: 'payment-services';
    id: string;
  };
  relationshipNames: string[];
};

/**
 * example:
 * {
 *  type: 'payment-instruments',
 *  id: '66e1179a8ae5d01e4c2fcecf',
 *  test: true,
 *  service_type: 'till_payments',
 *  expired: false,
 *  obfuscated_card: '411111******1111',
 *  expiry_month: 11,
 *  expiry_year: 2027,
 *  card_type: 'visa',
 *  service: {type: 'payment-services', id: '620ae0a760b75a4a4a001dd5'},
 *  relationshipNames: ['service'],
 * };
 */
export type TillPaymentInstrument = BasePaymentInstrument & {
  type: 'payment-instruments';
  service_type: PaymentServiceType.Till;
  obfuscated_card: string;
  expiry_month: number;
  expiry_year: number;
  card_type: string;
};

export type ZenGiftCardsPaymentInstrument = BasePaymentInstrument & {
  type: 'zen-gift-card-payment-instruments';
  service_type: PaymentServiceType.ZenGiftCards;
  obfuscated_card: string;
  zen_card_number: string;
  // MFL is returning `null` here so we are completing this information in the
  // backend with the information from the Zen API. The MFL API originally
  // returns this as a dollars string with 3 decimal points (¯\_(ツ)_/¯) and
  // we transform it then to cents
  balance: number; // cents
  // balance_at?: null | null; // Will this be supported by MFL? It's coming as `null`
};

export type CreateZenGiftCardPaymentInstrumentRequest = {
  service_id: string;
  order_id: string;
  zen_gift_card: {
    gift_card_number: string;
    gift_card_pin: string;
  };
};

export type CreateZenGiftCardPaymentInstrumentResponse = {
  type: string;
  id: string;
  iframe_challenge_url: null | string;
  payment_instrument: ZenGiftCardsPaymentInstrument;
  relationshipNames: string[];
};
